import React from "react";

import { graphql } from "gatsby";

import Button from "cms/components/Button";
import { ButtonVariant } from "cms/components/Button/Button.styles";
import OptimizedImage from "cms/components/OptimizedImage";
import RichText from "cms/components/RichText";
import Text from "cms/components/Text";
import OptimizedVideo from "index";

import {
  Container,
  Content,
  ContentProps,
  Illustration,
  IllustrationProps,
  Link,
  Message,
  Pitch,
  Tagline,
} from "./SectionWithImageBlock.styles";

export interface SectionWithImageBlockProps
  extends Queries.SanitySectionWithImageBlock {}

const SectionWithImageBlock: React.FC<SectionWithImageBlockProps> = ({
  tagline,
  headline,
  _rawPitch,
  button,
  image,
  imageAlt,
  video,
  videoAlt,
  reverseOrder,
  ...props
}) => {
  const layoutType = (props.layoutType ??
    "image-left") as ContentProps["$layoutType"];
  const variant = (props.variant ?? "standard") as ContentProps["$variant"];
  const imageFit = (props.imageFit ??
    "cover") as IllustrationProps["$imageFit"];
  const headlineElement = (props.headlineElement ?? "h2") as "h1" | "h2" | "h3";

  return (
    <Container>
      <Content
        $layoutType={layoutType}
        $reverseOrderOnMobil={reverseOrder}
        $variant={variant}
      >
        <Illustration
          $imageFit={imageFit}
          $layoutType={layoutType}
          $variant={variant}
        >
          {video && (
            <OptimizedVideo
              video={video}
              style={{
                width: "100%",
                maxHeight: "550px",
                borderRadius: "1.25rem",
              }}
              alt={videoAlt}
            />
          )}

          {!video && image && (
            <OptimizedImage image={image} alt={imageAlt} imageFit={imageFit} />
          )}
        </Illustration>
        <Message $layoutType={layoutType} $variant={variant}>
          {tagline && <Tagline>{tagline}</Tagline>}
          <Text variant="H2" as={headlineElement}>
            {headline}
          </Text>
          <Pitch>
            <RichText text={_rawPitch} />
          </Pitch>
          {button?.url && button.label && (
            <Link href={button.url}>
              <Button
                label={button.label}
                variant={button.variant as ButtonVariant}
              />
            </Link>
          )}
        </Message>
      </Content>
    </Container>
  );
};

export const query = graphql`
  fragment SectionWithImageBlock on SanitySectionWithImageBlock {
    _key
    _type
    variant
    layoutType
    headline
    headlineElement
    _rawPitch(resolveReferences: { maxDepth: 5 })
    tagline
    button {
      variant
      label
      url
    }
    image { ...OptimizedImage }
    imageAlt
    imageFit
    video { ...OptimizedVideo }
    videoAlt
    reverseOrder
  }
`;

export default SectionWithImageBlock;
